import { Badge as ShadcnBadge } from '@shadcn/ui/badge';
import { cva, type VariantProps } from 'class-variance-authority';
import { LucideIcon, X } from 'lucide-react';
import React from 'react';

const DEFAULT_ICON_SIZE = 16;

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-2xl font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 h-auto! whitespace-normal',
  {
    variants: {
      size: {
        sm: 'text-xs px-2 py-[3px] h-5',
        lg: 'text-sm px-3 py-1 h-7',
      },
      appearance: {
        aquamarine: 'bg-[var(--color-aquamarine-300)] text-typography',
        indigo: 'bg-[var(--color-indigo-300)] text-typography',
        mauve: 'bg-[var(--color-mauve-300)] text-typography',
        primary: 'bg-primary-700 text-white',
        secondary: 'bg-secondary-300 text-typography',
      },
    },
    defaultVariants: {
      size: 'sm',
      appearance: 'primary',
    },
  }
);

interface IBadgeProps extends React.ComponentProps<typeof ShadcnBadge>, VariantProps<typeof badgeVariants> {
  readonly icon?: LucideIcon;
  readonly onClearFunction?: () => void;
  readonly children?: React.ReactNode;
}

export function Badge({ icon: Icon, onClearFunction, children, size, appearance, className, ...props }: IBadgeProps) {
  const removeLabel = `Remove ${typeof children === 'string' ? children : ''}`;
  const variantsClassName = badgeVariants({ size, appearance, className });
  return (
    <ShadcnBadge className={variantsClassName} {...props}>
      {Icon && <Icon className="flex-shrink-0" size={DEFAULT_ICON_SIZE} />}
      {children}
      {onClearFunction && (
        <button
          aria-label={removeLabel}
          className="inline-flex items-center justify-center cursor-pointer ml-1 hover:opacity-75 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-1 rounded-full"
          onClick={(e) => {
            e.stopPropagation();
            onClearFunction();
          }}
          title={removeLabel}
          type="button"
        >
          <X aria-hidden="true" className="flex-shrink-0" size={DEFAULT_ICON_SIZE} />
        </button>
      )}
    </ShadcnBadge>
  );
}
